import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { initializeHttpProvider } from '@/providers/HttpProvider/initializeHttpProvider';
import { getBalance } from '@/redux/parcel/asyncRequest';
import { AppDispatch, RootState } from '@/redux/store';
import {
  Destination,
  HttpError,
  IResCreateOrderOk,
} from '@/types/parcel/createOrder/create.interface';
import { IResP99OK, NearestPoint } from '@/types/parcel/createOrder/p99.interface';
import {
  DeliveryType,
  DeliveryTypes,
  IResQuotationOK,
} from '@/types/parcel/createOrder/quotation.interface';
import { translateErrorMessage } from '@/utils/errorTranslations';

import { CreateOrderFormType } from './create-order-schema';

const renderErrorToastContent = (title: string, description?: string) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <div style={{ fontWeight: 600, fontSize: '14px' }}>{title}</div>
      {description && (
        <div
          style={{ fontSize: '12px', color: '#4a4a4a', fontWeight: 'normal', lineHeight: '1.4' }}
        >
          {description}
        </div>
      )}
    </div>
  );
};

export interface IQuotation {
  loading: boolean;
  error: string | null;
  data: DeliveryTypes[] | null;
}

const p99SelectedInitial = {
  address: '',
  businessName: '',
  distance: 0,
  latitude: 0,
  locationId: '',
  locationType: '',
  longitude: 0,
  schedule: {
    friday: { closed: false, closes: '', opens: '' },
    monday: { closed: false, closes: '', opens: '' },
    saturday: { closed: false, closes: '', opens: '' },
    sunday: { closed: false, closes: '', opens: '' },
    thursday: { closed: false, closes: '', opens: '' },
    timezone: '',
    tuesday: { closed: false, closes: '', opens: '' },
    wednesday: { closed: false, closes: '', opens: '' },
  },
  zipcode: '',
};

const formatCoord = (coord?: number | string) => {
  if (typeof coord === 'string') return coord;
  return coord === undefined ? '0' : coord.toFixed(8);
};

interface AddressFormattingInput {
  address?: string;
  externalNumber?: string | null;
  internalNumber?: string | null;
  cologne?: string | null;
  state?: string | null;
  showForm?: boolean;
}

const formatFullAddress = (data?: AddressFormattingInput): string => {
  if (!data?.address) return '';
  if (!data.showForm) return data.address;

  const parts: string[] = [];

  let streetAndNumbers = data.address.trim();
  if (data.externalNumber && data.externalNumber.trim()) {
    streetAndNumbers += ` ${data.externalNumber.trim()}`;
  }
  if (data.internalNumber && data.internalNumber.trim()) {
    streetAndNumbers += ` Int ${data.internalNumber.trim()}`;
  }
  if (streetAndNumbers) {
    parts.push(streetAndNumbers);
  }

  if (data.cologne && data.cologne.trim()) {
    parts.push(data.cologne.trim());
  }

  if (data.state && data.state.trim()) {
    parts.push(data.state.trim());
  }

  return parts.join(', ');
};

const useCreateOrder = () => {
  const { profile } = useSelector((state: RootState) => state.parcel);
  const dispatch = useDispatch<AppDispatch>();
  const [hasQuot, setHasQuot] = useState(false);
  const [quot, setQuot] = useState<IQuotation>({
    loading: false,
    error: null,
    data: null,
  });

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [orderSuccessData, setOrderSuccessData] = useState<{
    trackingId: string;
    internalKey: string;
    isDraft: boolean;
    deliveryType: string;
  } | null>(null);
  const [createDisabled, setCreateDisabled] = useState(true);
  const [loadingCreate, setLoadingCreate] = useState(false);
  const [createData, setCreateData] = useState<CreateOrderFormType>();
  const [resetForm, setResetForm] = useState(false);
  const [p99Data, setP99Data] = useState<NearestPoint[]>();
  const [p99Selected, setP99Selected] = useState<NearestPoint>(p99SelectedInitial);
  const [coordinatesPoint, setCoordinatesPoint] = useState<Destination[]>([]);

  // start querys
  const createOrder = async () => {
    const http = initializeHttpProvider();
    setLoadingCreate(true);
    const isLocalPickup = ['SMD', 'SPT', 'RET'].includes(createData?.deliveryType as string);
    const isDraft = isLocalPickup ? Boolean(createData?.draft) : false;
    const pickUpAfter = isLocalPickup && !isDraft ? createData?.options.pickUpAfter || '' : '';

    try {
      const response = (await http.createOrder.create({
        origin: {
          address: formatFullAddress(createData?.origin),
          country: profile.data?.country,
          lat: formatCoord(createData?.origin.lat),
          lng: formatCoord(createData?.origin.lng),
          zipCode: createData?.origin.zipCode,
          city: createData?.origin.city,
          references: createData?.origin.reference,
        },
        destination: {
          address: formatFullAddress(createData?.destination),
          country: profile.data?.country,
          lat: formatCoord(createData?.destination.lat),
          lng: formatCoord(createData?.destination.lng),
          zipCode: createData?.destination.zipCode,
          city: createData?.destination.city,
          references: createData?.destination.reference,
        },
        deliveryType: createData?.deliveryType as DeliveryType,
        additional: {
          insurance: Number(createData?.payments.insuredValue) || 0,
          cod: Number(createData?.payments?.cashOnDelivery?.amount) || 0,
          draft: isDraft,
          ecoTag: false,
          notes: createData?.options.notes,
          pickUpAfter: pickUpAfter,
          weight: createData?.items.weight,
        },
        size: createData?.items.size || '',
        internalKey: createData?.internalKey?.trim() || '',
        recipient: {
          email: createData?.recipient.email || '',
          firstName: createData?.recipient.firstName || '',
          lastName: createData?.recipient.lastName || '',
          phone: createData?.recipient.phone || '',
        },
        sender: {
          email: createData?.sender.email || '',
          firstName: createData?.sender.firstName || '',
          lastName: createData?.sender.lastName || '',
          phone: createData?.sender.phone || '',
        },
      })) as IResCreateOrderOk;

      setLoadingCreate(false);
      setResetForm(true);
      setHasQuot(false);
      setCreateDisabled(true);

      toast.success('Orden creada con éxito');

      if (response?.data?.orderData) {
        const { trackingId, internalKey, additional, deliveryType } = response.data.orderData;
        setOrderSuccessData({
          trackingId,
          internalKey,
          isDraft: additional?.draft || false,
          deliveryType,
        });
        setShowSuccessModal(true);
      }

      // Refetch del balance si es cuenta prepago
      const isPrepaid = profile.data?.configuration.paymentType === 'PREPAID';
      if (isPrepaid) {
        dispatch(getBalance());
      }

      // Guardar dirección de origen en Saved Addresses si fue marcado
      if (createData?.saveOriginAddress && createData?.origin?.address) {
        const originAlias =
          `${createData.sender.firstName} ${createData.sender.lastName}`.trim() ||
          createData.origin.address;
        void http.userData
          .createSavedAddress({
            alias: originAlias,
            type: 'origin',
            address: createData.origin.address,
            street_number: createData.origin.externalNumber || undefined,
            interior_number: createData.origin.internalNumber || undefined,
            colony: createData.origin.cologne || undefined,
            zip_code: createData.origin.zipCode || '',
            city: createData.origin.city || '',
            state: createData.origin.state || undefined,
            country: createData.origin.country || profile.data?.country || 'MEX',
            latitude: parseFloat(createData.origin.lat || '0'),
            longitude: parseFloat(createData.origin.lng || '0'),
            references: createData.origin.reference || undefined,
            contact: {
              first_name: createData.sender.firstName,
              last_name: createData.sender.lastName,
              phone: createData.sender.phone,
              email: createData.sender.email,
            },
          })
          .catch((err) => console.error('Error guardando dirección de origen:', err));
      }

      // Guardar dirección de destino en Saved Addresses si fue marcado
      if (createData?.saveDestinationAddress && createData?.destination?.address) {
        const destAlias =
          `${createData.recipient.firstName} ${createData.recipient.lastName}`.trim() ||
          createData.destination.address;
        void http.userData
          .createSavedAddress({
            alias: destAlias,
            type: 'destination',
            address: createData.destination.address,
            street_number: createData.destination.externalNumber || undefined,
            interior_number: createData.destination.internalNumber || undefined,
            colony: createData.destination.cologne || undefined,
            zip_code: createData.destination.zipCode || '',
            city: createData.destination.city || '',
            state: createData.destination.state || undefined,
            country: createData.destination.country || profile.data?.country || 'MEX',
            latitude: parseFloat(createData.destination.lat || '0'),
            longitude: parseFloat(createData.destination.lng || '0'),
            references: createData.destination.reference || undefined,
            contact: {
              first_name: createData.recipient.firstName,
              last_name: createData.recipient.lastName,
              phone: createData.recipient.phone,
              email: createData.recipient.email,
            },
          })
          .catch((err) => console.error('Error guardando dirección de destino:', err));
      }
    } catch (error) {
      setResetForm(false);
      setLoadingCreate(false);

      let msg = '';
      let traceId = '';

      const httpError = error as HttpError;
      if (httpError && typeof httpError === 'object' && httpError.response?.json) {
        try {
          const errorData = await httpError.response.json();
          msg = errorData.message || '';
          traceId = errorData.traceId || '';
        } catch {
          // Si no se puede parsear el error, usar mensaje por defecto
        }
      } else if (error instanceof Error) {
        msg = error.message;
      } else if (typeof error === 'string') {
        msg = error;
      } else if (error && typeof error === 'object' && 'message' in error) {
        msg = String((error as Record<string, unknown>).message);
      }

      let errorDescription = translateErrorMessage(msg, '');

      const internalKeyVal = createData?.internalKey?.trim() || '';
      if (internalKeyVal && internalKeyVal.length < 5) {
        errorDescription = 'El identificador debe tener al menos 5 caracteres.';
      }

      if (
        !errorDescription ||
        errorDescription.trim() === '' ||
        errorDescription === 'Error desconocido'
      ) {
        errorDescription =
          'No pudimos crear tu orden. Por favor, revisa la información del destinatario, las dimensiones del paquete y la cobertura antes de intentar nuevamente.';
      }

      if (traceId) {
        if (!errorDescription.endsWith('.')) {
          errorDescription += '.';
        }
        errorDescription += ` Código de soporte: ${traceId}`;
      }

      toast.error(renderErrorToastContent('Error al crear la orden', errorDescription), {
        autoClose: 6000,
      });
      setCreateDisabled(false);
    }
  };

  const resetQuotation = () => {
    setQuot({ loading: false, error: null, data: null });
    setHasQuot(false);
    setCreateDisabled(true);
  };

  const getQuotation = async (value: CreateOrderFormType) => {
    const http = initializeHttpProvider();
    try {
      setQuot((prev) => ({ ...prev, loading: true, error: null }));

      const response = await http.createOrder.quotation({
        origin: {
          address: formatFullAddress(value.origin),
          country: profile.data?.country || '',
          latitude: formatCoord(value.origin.lat),
          longitude: formatCoord(value.origin.lng),
          zipCode: value.origin.zipCode || '',
        },
        destination: {
          address: formatFullAddress(value.destination),
          country: profile.data?.country || '',
          latitude: formatCoord(value.destination.lat),
          longitude: formatCoord(value.destination.lng),
          zipCode: value.destination.zipCode || '',
        },
        deliveryType: undefined,
        insurance: value.payments?.insured ? Number(value.payments.insuredValue) || 0 : 0,
        cod: value.payments?.cashOnDelivery?.cod
          ? Number(value.payments?.cashOnDelivery?.amount) || 0
          : 0,
        size: value.items.size,
      });

      setCreateData(value);
      setQuot({
        loading: false,
        error: null,
        data: (response as IResQuotationOK).data.deliveryTypes,
      });
      setCreateDisabled(false);
      setHasQuot(true);
    } catch (e) {
      let msg =
        'No pudimos obtener una cotización. Por favor revisa los datos e intenta nuevamente.';
      let traceId = '';

      const httpError = e as HttpError;
      if (httpError.response?.json) {
        try {
          const errorData = await httpError.response.json();
          msg = errorData.message || msg;
          traceId = errorData.traceId || '';
        } catch {
          // Si no se puede parsear el error, usar mensaje por defecto
        }
      }

      if (traceId) {
        if (!msg.endsWith('.')) {
          msg += '.';
        }
        msg += ` Código de soporte: ${traceId}`;
      }

      toast.error(msg);
      setQuot((prev) => ({
        ...prev,
        error: msg,
        data: null,
      }));
      setCreateDisabled(true);
      setHasQuot(false);
    } finally {
      setQuot((prev) => ({ ...prev, loading: false }));
    }
  };

  const getP99 = async ({ latitude, longitude }: { latitude?: number; longitude?: number }) => {
    const http = initializeHttpProvider();
    try {
      const response = await http.createOrder.p99({
        latitude: latitude || 0,
        longitude: longitude || 0,
        country: profile.data?.country || 'MEX',
      });

      const data = (response as IResP99OK).data;
      setP99Data(data);
      const nearestPoint = data.reduce(
        (min, current) => (current.distance < min.distance ? current : min),
        data[0],
      );
      setP99Selected(nearestPoint);
    } catch (error) {
      // Error silencioso - getP99 no requiere notificación al usuario
      console.error(error);
    }
  };
  // end querys

  const handleHasQuot = () => {
    setHasQuot(false);
    setCreateDisabled(true);
  };

  const handleCoordinatesPoint = (value: Destination, type: 'origin' | 'destination') => {
    setCoordinatesPoint((prev) => {
      const newCoordinates = [...prev];
      newCoordinates[type === 'origin' ? 0 : 1] = value;
      return newCoordinates;
    });
  };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    setOrderSuccessData(null);
  };

  const handlePrintLabel = async () => {
    if (!orderSuccessData?.trackingId) return;

    const http = initializeHttpProvider();
    const trackingId = orderSuccessData.trackingId;

    toast.promise(
      async () => {
        const response = await http.orders.labels({
          pageSize: 'letter',
          trackingIds: [trackingId],
        });
        if (!('data' in response) || !('url' in response.data)) {
          throw new Error('Error al descargar la etiqueta');
        }

        const fileUrl = response.data.url;
        const fileName = `guia_${trackingId}.pdf`;

        try {
          // Descarga directa vía Blob (fuerza la descarga inmediata al disco)
          const fileRes = await fetch(fileUrl);
          const blob = await fileRes.blob();
          const blobUrl = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(blobUrl);
        } catch {
          // Fallback robusto mediante trigger de enlace directo
          const link = document.createElement('a');
          link.href = fileUrl;
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      },
      {
        pending: 'Descargando etiqueta...',
        success: 'Etiqueta descargada con éxito',
        error: 'Ha ocurrido un error al intentar descargar la etiqueta',
      },
    );
  };

  return {
    getQuotation,
    resetQuotation,
    quot,
    createOrder,
    loadingCreate,
    resetForm,
    createDisabled,
    hasQuot,
    handleHasQuot,
    getP99,
    p99Data,
    p99Selected,
    handleCoordinatesPoint,
    coordinatesPoint,
    showSuccessModal,
    orderSuccessData,
    handleCloseSuccessModal,
    handlePrintLabel,
    createData,
    setCreateData,
  };
};

export default useCreateOrder;
